import { predictionAdminApis, taskAdminApis } from '@gleamer/apis';
import { PredictionAdmin, TaskAdmin } from '@gleamer/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthorization } from './useImports';
import { PREDICTION_PRODUCTS_KEY } from './usePredictionProducts';
import { PREDICTION_IMPORT_KEY } from './useTaskActivity';

const {
  changePredictionImportProduct,
  deletePredictionImport,
  fetchPredictionImportPatients,
  fetchPredictionImportStudies,
  fetchPredictionFileImports,
  fetchPredictionFiles,
  importPredictionFile,
  requestPredictionUpload,
  uploadPredictionFile,
} = predictionAdminApis;

export const PREDICTION_FILE_IMPORTS_KEY = 'prediction-file-imports';
export const PREDICTION_FILES_KEY = 'prediction-files';
export const PREDICTION_IMPORT_TREE_KEY = 'prediction-import-tree';
/** How often the list is read again while an import is in flight, and when none is. */
export const IN_FLIGHT_POLL_MS = 3000;
export const IDLE_POLL_MS = 30000;

export function inFlight(imported: TaskAdmin.PredictionImport): boolean {
  return imported.status === 'REQUESTED' || imported.status === 'IMPORTING';
}

/** A page of the file imports, newest first, read again every few seconds while one of them is running. */
export function usePredictionFileImports(query: {
  productId?: number;
  limit: number;
  offset: number;
}) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [PREDICTION_FILE_IMPORTS_KEY, query],
    queryFn: () => fetchPredictionFileImports(auth, query),
    keepPreviousData: true,
    // a waiting import is requested as soon as the product's running one settles
    refetchInterval: data =>
      data?.some(i => inFlight(i) || i.status === 'WAITING') ? IN_FLIGHT_POLL_MS : IDLE_POLL_MS,
  });
}

/** A page of the files dropped in the bucket, newest first; read again while one is still being read. */
export function usePredictionFiles(query: { limit: number; offset: number }) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [PREDICTION_FILES_KEY, query],
    queryFn: () => fetchPredictionFiles(auth, query),
    keepPreviousData: true,
    refetchInterval: data =>
      data?.some(f => f.status === 'LOADING') ? IN_FLIGHT_POLL_MS : IDLE_POLL_MS,
  });
}

export type FileImport = Omit<PredictionAdmin.PredictionFileImportRequest, 'object'> & {
  file: File;
};

/**
 * A file imported into a product: where to upload it asked for, the file PUT there, then its import asked for.
 * The list and the products are read again once it is requested.
 */
export function useImportPredictionFile() {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, ...request }: FileImport) => {
      const upload = await requestPredictionUpload(auth, file.name);
      await uploadPredictionFile(upload, file);
      return importPredictionFile(auth, { ...request, object: upload.object });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREDICTION_FILE_IMPORTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PREDICTION_PRODUCTS_KEY] });
    },
  });
}

/** A page of an import's patients, with what each holds. */
export function usePredictionImportPatients(importId: number, page: { limit: number; offset: number }) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [PREDICTION_IMPORT_TREE_KEY, importId, page],
    queryFn: () => fetchPredictionImportPatients(auth, importId, page),
    keepPreviousData: true,
  });
}

/** One patient's studies, series and images in an import; read when the patient is opened. */
export function usePredictionImportStudies(importId: number, patientId: string) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [PREDICTION_IMPORT_TREE_KEY, importId, 'patient', patientId],
    queryFn: () => fetchPredictionImportStudies(auth, importId, patientId),
  });
}

export type ImportDecision =
  | { type: TaskAdmin.PredictionImportAction }
  | { type: 'product'; productId: number }
  | { type: 'delete' };

/**
 * A data manager's decision on a file import — replay, accept, abandon, change of product, delete —; then the list,
 * the import, its tree and the products (their counts) are read again.
 */
export function usePredictionImportDecision(importId: number) {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decision: ImportDecision): Promise<unknown> => {
      switch (decision.type) {
        case 'product':
          return changePredictionImportProduct(auth, importId, decision.productId);
        case 'delete':
          return deletePredictionImport(auth, importId);
        default:
          return taskAdminApis.settlePredictionImport(auth, importId, decision.type);
      }
    },
    onSuccess: () => {
      [PREDICTION_FILE_IMPORTS_KEY, PREDICTION_IMPORT_KEY, PREDICTION_IMPORT_TREE_KEY, PREDICTION_PRODUCTS_KEY].forEach(key =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
  });
}
